<!-- =========================================================================
     assessment-report.component.html  — Module 11
     Two-column layout: Left = report info + summary, Right = chat assistant
     ========================================================================= -->

<!-- =====================================================================
     LOADING STATE
     ===================================================================== -->
<div class="loader-overlay" *ngIf="pageState() === 'loading'" aria-live="polite">
  <div class="loader-header">
    <h2 class="loader-header__title">Assessing Your Application</h2>
    <p class="loader-header__sub">
      ☕ Sit back and relax — the AI is reviewing every guardrail against
      your uploaded artifacts. This may take a few minutes.
    </p>
  </div>

  <div class="spinner-wrap">
    <svg class="spinner" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="28" cy="28" r="24" stroke="#E6E6E6" stroke-width="5"/>
      <circle cx="28" cy="28" r="24" stroke="url(#assessSpinGrad)" stroke-width="5"
              stroke-linecap="round" stroke-dasharray="150.8" stroke-dashoffset="37.7" class="spinner__arc"/>
      <defs>
        <linearGradient id="assessSpinGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stop-color="#0056A2"/>
          <stop offset="100%" stop-color="#19E3C5"/>
        </linearGradient>
      </defs>
    </svg>
    <span class="spinner__pct">{{ progress() }}%</span>
  </div>

  <div class="loader-bar-row">
    <div class="loader-bar"><div class="loader-bar__fill" [style.width.%]="progress()"></div></div>
  </div>

  <div class="phase-list">
    <!-- Phase 1 — always done -->
    <div class="phase-card phase-card--done">
      <div class="phase-icon">✅</div>
      <div class="phase-body">
        <div class="phase-row">
          <span class="phase-num">Phase 1</span>
          <span class="phase-badge phase-badge--done">Complete</span>
        </div>
        <span class="phase-title">Category Selection</span>
        <span class="phase-detail">{{ finalizedCategories.length }} categories selected.</span>
      </div>
    </div>

    <!-- Phase 2 — Retrieval -->
    <div class="phase-card"
         [class.phase-card--active]="activePhaseKey() === 'retrieving'"
         [class.phase-card--done]="isPhaseComplete('retrieving')">
      <div class="phase-icon" *ngIf="isPhaseComplete('retrieving')">✅</div>
      <div class="phase-icon phase-icon--spin" *ngIf="activePhaseKey() === 'retrieving' && !isPhaseComplete('retrieving')">📚</div>
      <div class="phase-icon phase-icon--pending" *ngIf="activePhaseKey() !== 'retrieving' && !isPhaseComplete('retrieving')">⏳</div>
      <div class="phase-body">
        <div class="phase-row">
          <span class="phase-num">Phase 2</span>
          <span class="phase-badge phase-badge--done"    *ngIf="isPhaseComplete('retrieving')">Complete</span>
          <span class="phase-badge phase-badge--active"  *ngIf="activePhaseKey() === 'retrieving' && !isPhaseComplete('retrieving')">In Progress</span>
          <span class="phase-badge phase-badge--pending" *ngIf="activePhaseKey() !== 'retrieving' && !isPhaseComplete('retrieving')">Pending</span>
        </div>
        <span class="phase-title">Guardrail Retrieval</span>
        <!-- Show live status only when this phase is active -->
        <span class="phase-detail" *ngIf="activePhaseKey() === 'retrieving' && !isPhaseComplete('retrieving')">{{ statusMsg() }}</span>
        <span class="phase-detail" *ngIf="isPhaseComplete('retrieving')">Guardrails fetched and artifact evidence matched.</span>
        <span class="phase-detail phase-detail--muted" *ngIf="activePhaseKey() !== 'retrieving' && !isPhaseComplete('retrieving')">Waiting to start…</span>
      </div>
    </div>

    <!-- Phase 3 — AI Assessment -->
    <div class="phase-card"
         [class.phase-card--active]="activePhaseKey() === 'assessing'"
         [class.phase-card--done]="isPhaseComplete('assessing')">
      <div class="phase-icon" *ngIf="isPhaseComplete('assessing')">✅</div>
      <div class="phase-icon phase-icon--spin" *ngIf="activePhaseKey() === 'assessing' && !isPhaseComplete('assessing')">🤖</div>
      <div class="phase-icon phase-icon--pending" *ngIf="activePhaseKey() !== 'assessing' && !isPhaseComplete('assessing')">⏳</div>
      <div class="phase-body">
        <div class="phase-row">
          <span class="phase-num">Phase 3</span>
          <span class="phase-badge phase-badge--done"    *ngIf="isPhaseComplete('assessing')">Complete</span>
          <span class="phase-badge phase-badge--active"  *ngIf="activePhaseKey() === 'assessing' && !isPhaseComplete('assessing')">In Progress</span>
          <span class="phase-badge phase-badge--pending" *ngIf="activePhaseKey() !== 'assessing' && !isPhaseComplete('assessing')">Pending</span>
        </div>
        <span class="phase-title">AI Guardrail Assessment</span>
        <!-- Show live status only when this phase is active -->
        <span class="phase-detail" *ngIf="activePhaseKey() === 'assessing' && !isPhaseComplete('assessing')">{{ statusMsg() }}</span>
        <span class="phase-detail" *ngIf="isPhaseComplete('assessing')">All guardrails assessed by the AI.</span>
        <span class="phase-detail phase-detail--muted" *ngIf="activePhaseKey() !== 'assessing' && !isPhaseComplete('assessing')">Will start once retrieval is complete…</span>
      </div>
    </div>

    <!-- Phase 4 — Compiling -->
    <div class="phase-card"
         [class.phase-card--active]="activePhaseKey() === 'compiling'"
         [class.phase-card--done]="isPhaseComplete('compiling')">
      <div class="phase-icon" *ngIf="isPhaseComplete('compiling')">✅</div>
      <div class="phase-icon phase-icon--spin" *ngIf="activePhaseKey() === 'compiling' && !isPhaseComplete('compiling')">📊</div>
      <div class="phase-icon phase-icon--pending" *ngIf="activePhaseKey() !== 'compiling' && !isPhaseComplete('compiling')">⏳</div>
      <div class="phase-body">
        <div class="phase-row">
          <span class="phase-num">Phase 4</span>
          <span class="phase-badge phase-badge--done"    *ngIf="isPhaseComplete('compiling')">Complete</span>
          <span class="phase-badge phase-badge--active"  *ngIf="activePhaseKey() === 'compiling' && !isPhaseComplete('compiling')">In Progress</span>
          <span class="phase-badge phase-badge--pending" *ngIf="activePhaseKey() !== 'compiling' && !isPhaseComplete('compiling')">Pending</span>
        </div>
        <span class="phase-title">Compiling Report</span>
        <span class="phase-detail" *ngIf="activePhaseKey() === 'compiling' && !isPhaseComplete('compiling')">{{ statusMsg() }}</span>
        <span class="phase-detail" *ngIf="isPhaseComplete('compiling')">Report compiled and ready.</span>
        <span class="phase-detail phase-detail--muted" *ngIf="activePhaseKey() !== 'compiling' && !isPhaseComplete('compiling')">Finalisation happens after all guardrails are assessed…</span>
      </div>
    </div>
  </div>
</div><!-- /loader-overlay -->


<!-- =====================================================================
     ERROR STATE
     ===================================================================== -->
<div class="report-page" *ngIf="pageState() === 'error'">
  <div class="error-shell">
    <div class="error-card">
      <div class="error-icon">⚠️</div>
      <h2 class="error-title">Assessment Failed</h2>
      <p class="error-msg">{{ errorMsg() }}</p>
      <div class="error-actions">
        <button class="btn btn--ghost" (click)="goBack()">← Back</button>
        <button class="btn btn--primary" (click)="runAssessment()">Retry</button>
      </div>
    </div>
  </div>
</div>


<!-- =====================================================================
     READY STATE — two-column report layout
     ===================================================================== -->
<div class="report-page" *ngIf="pageState() === 'ready'">

  <!-- ── Main two-column grid ────────────────────────────────────────── -->
  <div class="report-grid">

    <!-- ── LEFT PANEL ──────────────────────────────────────────────── -->
    <div class="left-panel">

      <!-- Report info card -->
      <div class="info-card">
        <h3 class="info-card__heading">Analysis Report</h3>
        <div class="info-card__content">
          <p class="info-card__report-title">Guardrails Analysis Report</p>
          <div class="info-card__file">
            <span class="file-icon">📄</span>
            <span class="file-name">{{ reportFileName() }}</span>
          </div>
          <p class="info-card__date" *ngIf="generatedDate()">Generated: {{ generatedDate() }}</p>
        </div>
      </div>

      <!-- Guardrails Summary card -->
      <div class="summary-card">
        <h3 class="summary-card__heading">Guardrails Summary</h3>
        <div class="summary-grid">

          <!-- Met -->
          <div class="score-box score-box--met"
               (click)="openModal('Met', 'Met Guardrails')"
               tabindex="0" (keydown.enter)="openModal('Met', 'Met Guardrails')"
               role="button" aria-label="View Met guardrails">
            <span class="score-box__value">{{ metCount() }}</span>
            <span class="score-box__label">MET</span>
          </div>

          <!-- Partially Met -->
          <div class="score-box score-box--partial"
               (click)="openModal('Partially Met', 'Partially Met Guardrails')"
               tabindex="0" (keydown.enter)="openModal('Partially Met', 'Partially Met Guardrails')"
               role="button" aria-label="View Partially Met guardrails">
            <span class="score-box__value">{{ partialCount() }}</span>
            <span class="score-box__label">PARTIALLY MET</span>
          </div>

          <!-- Not Met -->
          <div class="score-box score-box--not-met"
               (click)="openModal('Not Met', 'Not Met Guardrails')"
               tabindex="0" (keydown.enter)="openModal('Not Met', 'Not Met Guardrails')"
               role="button" aria-label="View Not Met guardrails">
            <span class="score-box__value">{{ notMetCount() }}</span>
            <span class="score-box__label">NOT MET</span>
          </div>

          <!-- Not Applicable -->
          <div class="score-box score-box--na"
               (click)="openModal('Not Applicable', 'Not Applicable Guardrails')"
               tabindex="0" (keydown.enter)="openModal('Not Applicable', 'Not Applicable Guardrails')"
               role="button" aria-label="View Not Applicable guardrails">
            <span class="score-box__value">{{ naCount() }}</span>
            <span class="score-box__label">NOT APPLICABLE</span>
          </div>

          <!-- Total -->
          <div class="score-box score-box--total"
               (click)="openModal('all', 'All Guardrails')"
               tabindex="0" (keydown.enter)="openModal('all', 'All Guardrails')"
               role="button" aria-label="View all guardrails">
            <span class="score-box__value">{{ totalCount() }}</span>
            <span class="score-box__label">TOTAL</span>
          </div>

        </div><!-- /summary-grid -->
      </div><!-- /summary-card -->

    </div><!-- /left-panel -->


    <!-- ── RIGHT PANEL — Analysis Assistant ────────────────────────── -->
    <div class="right-panel">
      <div class="chat-panel">

        <div class="chat-header">
          <h3 class="chat-header__title">Analysis Assistant</h3>
          <p class="chat-header__sub">Ask questions about your report, uploaded documents, or guardrails</p>
        </div>

        <div class="chat-messages" #chatScroll>
          <ng-container *ngFor="let msg of chatMessages()">
            <!-- Skip the empty assistant placeholder while streaming hasn't started yet -->
            <div
              *ngIf="!(msg.role === 'assistant' && msg.text === '')"
              class="chat-bubble"
              [class.chat-bubble--user]="msg.role === 'user'"
              [class.chat-bubble--assistant]="msg.role === 'assistant'">
              <!-- User messages: plain text; Assistant: rendered markdown -->
              <span *ngIf="msg.role === 'user'"
                    class="chat-bubble__text">{{ msg.text }}</span>
              <div *ngIf="msg.role === 'assistant'"
                   class="chat-bubble__text chat-bubble__md"
                   [innerHTML]="msg.text | markdown"></div>
            </div>
          </ng-container>
          <!-- Typing indicator while waiting for LLM response -->
          <div *ngIf="chatLoading()" class="chat-bubble chat-bubble--assistant chat-bubble--typing">
            <span class="chat-bubble__text">
              <span class="typing-dot"></span>
              <span class="typing-dot"></span>
              <span class="typing-dot"></span>
            </span>
          </div>
        </div>

        <div class="chat-input-row">
          <input
            class="chat-input"
            type="text"
            placeholder="Ask about your analysis results..."
            [value]="chatInput()"
            [disabled]="chatLoading()"
            (input)="chatInput.set($any($event.target).value)"
            (keydown)="onChatKeydown($event)"
          />
          <button
            class="btn btn--primary chat-send-btn"
            (click)="sendChat()"
            [disabled]="chatLoading()">
            {{ chatLoading() ? '…' : 'Send' }}
          </button>
        </div>

      </div>
    </div><!-- /right-panel -->

  </div><!-- /report-grid -->


  <!-- ── Bottom action bar ──────────────────────────────────────────── -->
  <div class="bottom-bar">
    <button class="btn btn--ghost" (click)="startNewAssessment()">
      🔄 Start New Assessment
    </button>
    <button
      class="btn btn--primary"
      (click)="downloadReport()"
      [disabled]="downloadingReport()">
      <span *ngIf="!downloadingReport()">📥 Download Report</span>
      <span *ngIf="downloadingReport()">⏳ Preparing…</span>
    </button>
    <a
      class="btn btn--feedback btn--heartbeat"
      href="https://forms.office.com/r/MWFCib2cwS"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Give Feedback">
      💬 Give Feedback
    </a>
    <button class="btn btn--secondary" (click)="goBack()">
      ← Back to Category Selection
    </button>
  </div>

  <!-- ── AI Disclaimer ─────────────────────────────────────────────────── -->
  <p class="ai-disclaimer">
    Content generated by AI may be inaccurate. Please review all results carefully before using them in any official capacity.
  </p>

</div><!-- /report-page -->


<!-- =====================================================================
     GUARDRAIL DETAIL MODAL
     ===================================================================== -->
<div class="modal-backdrop" *ngIf="modalOpen()" role="dialog" aria-modal="true">
  <div class="modal" (mousedown)="$event.stopPropagation()" (mouseup)="$event.stopPropagation()" (click)="$event.stopPropagation()">

    <!-- Modal header -->
    <div class="modal__header">
      <h2 class="modal__title">{{ modalTitle() }} <span class="modal__count">({{ modalRows().length }})</span></h2>
      <button class="modal__close" (click)="closeModal()" aria-label="Close modal">✕</button>
    </div>

    <!-- Category filter chips (only when > 1 category present) -->
    <div class="modal__filters" *ngIf="modalCategories().length > 1">
      <span class="modal__filter-label">Filter by category:</span>
      <div class="modal__chips">
        <button
          class="chip"
          [class.chip--active]="modalCatFilter() === null"
          (click)="modalCatFilter.set(null)">
          All
        </button>
        <button
          *ngFor="let cat of modalCategories()"
          class="chip"
          [class.chip--active]="modalCatFilter() === cat"
          (click)="modalCatFilter.set(cat)">
          {{ cat }}
        </button>
      </div>
    </div>

    <!-- Scrollable content -->
    <div class="modal__body">

      <div *ngIf="modalRows().length === 0" class="modal__empty">
        No guardrails match the selected filter.
      </div>

      <!-- Guardrail entries -->
      <div
        *ngFor="let row of modalRows(); let i = index; trackBy: trackById"
        class="gr-card"
        [class.gr-card--met]="row.status === 'Met'"
        [class.gr-card--partial]="row.status === 'Partially Met'"
        [class.gr-card--not-met]="row.status === 'Not Met'"
        [class.gr-card--na]="row.status === 'Not Applicable'"
        [class.gr-card--edited]="row._edited">

        <!-- Card header -->
        <div class="gr-card__head">
          <div class="gr-card__meta">
            <span class="gr-cat-badge">{{ row.category }}</span>
            <span *ngIf="row.sl" class="gr-sl-badge">SL: {{ row.sl }}</span>
            <span *ngIf="row._edited" class="gr-edited-badge">✏ Edited</span>
          </div>
          <div class="gr-card__actions" *ngIf="!isEditing(row)">
            <span class="status-pill" [ngClass]="getStatusCss(row.status)">
              {{ getStatusIcon(row.status) }} {{ row.status }}
            </span>
            <button class="btn btn--edit" (click)="startEdit(row)">✏ Edit</button>
          </div>
        </div>

        <!-- Guardrail requirement (always shown, read-only) -->
        <div class="gr-card__section">
          <span class="gr-card__label">Requirement</span>
          <p class="gr-card__text">{{ row.guardrail }}</p>
        </div>

        <!-- ── VIEW MODE ─────────────────────────────────────────────── -->
        <ng-container *ngIf="!isEditing(row)">

          <div class="gr-card__section">
            <span class="gr-card__label">Justification</span>
            <div class="llm-error" *ngIf="isLlmError(row.justification)">
              ⚠️ There may have been an error communicating with the AI service. Please retry.
            </div>
            <p class="gr-card__text" *ngIf="!isLlmError(row.justification)">{{ row.justification }}</p>
          </div>

          <div class="gr-card__section" *ngIf="row.proof && row.proof.length > 0 && !row.artifacts_text">
            <span class="gr-card__label">Related Artifacts</span>
            <div class="proof-list">
              <div *ngFor="let p of row.proof" class="proof-item">
                <span class="proof-item__doc">📄 {{ p.document_name }}</span>
                <span class="proof-item__page">p.{{ p.page_number }}</span>
                <blockquote class="proof-item__quote">"{{ p.referenced_text }}"</blockquote>
              </div>
            </div>
          </div>

          <div class="gr-card__section" *ngIf="row.artifacts_text">
            <span class="gr-card__label">Related Artifacts <span class="gr-edited-badge">✏ Edited</span></span>
            <p class="gr-card__text" style="white-space: pre-wrap;">{{ row.artifacts_text }}</p>
          </div>

          <div class="gr-card__section" *ngIf="(!row.proof || row.proof.length === 0) && !row.artifacts_text">
            <span class="gr-card__label">Related Artifacts</span>
            <p class="gr-card__text gr-card__text--muted">No specific artifact evidence cited.</p>
          </div>

          <div class="gr-card__confidence">
            <span class="gr-card__label">Confidence</span>
            <div class="conf-bar">
              <div class="conf-bar__fill" [ngClass]="getStatusCss(row.status)" [style.width.%]="row.confidence_score"></div>
            </div>
            <span class="conf-pct">{{ row.confidence_score }}%</span>
          </div>

        </ng-container>

        <!-- ── EDIT MODE ──────────────────────────────────────────────── -->
        <ng-container *ngIf="isEditing(row)">

          <div class="edit-form">

            <div class="edit-form__group">
              <label class="edit-form__label">Status</label>
              <select class="edit-form__select" [(ngModel)]="editDraft.status">
                <option *ngFor="let opt of statusOptions" [value]="opt">{{ opt }}</option>
              </select>
            </div>

            <div class="edit-form__group">
              <label class="edit-form__label">Justification</label>
              <textarea
                class="edit-form__textarea"
                rows="5"
                [(ngModel)]="editDraft.justification"
                placeholder="Update justification…">
              </textarea>
            </div>

            <div class="edit-form__group">
              <label class="edit-form__label">Confidence Score (0–100)</label>
              <input
                class="edit-form__input"
                type="number"
                min="0" max="100"
                [(ngModel)]="editDraft.confidence_score"
              />
            </div>

            <div class="edit-form__group">
              <label class="edit-form__label">Related Artifacts</label>
              <p class="edit-form__hint" style="font-size:0.78rem;color:#666;margin:0 0 4px;">
                Override the AI-generated artifact evidence. Leave blank to keep the original references.
              </p>
              <textarea
                class="edit-form__textarea"
                rows="4"
                [(ngModel)]="editDraft.artifacts_text"
                placeholder="e.g. Security Policy v2.pdf (p.4): Encryption at rest is enforced…">
              </textarea>
            </div>

            <div class="edit-form__actions">
              <button class="btn btn--ghost btn--sm" (click)="cancelEdit()">Cancel</button>
              <button class="btn btn--primary btn--sm" (click)="saveEdit(row.guardrail_id)">💾 Save</button>
            </div>

          </div>

        </ng-container>

      </div><!-- /gr-card -->

    </div><!-- /modal__body -->

  </div><!-- /modal -->
</div><!-- /modal-backdrop -->
